/**
 * Frontend mirror of the backend `shared/userRoles.ts` helpers.
 * Keeps role-check logic in one place. See multi-tenancy.md
 * §"User identity model" for the unified-roles design.
 */

import type { UserRole } from '../api/auth';

export interface RoleSource {
    roles?: UserRole[];
    isSuperAdmin?: boolean;
}

/** Any tenant-scoped admin role OR super-admin. */
export function isAdmin(source: RoleSource): boolean {
    if (source.isSuperAdmin) return true;
    return !!source.roles?.some(r => r.endsWith('_admin') && r !== 'super_admin');
}

export function isTenantAdmin(source: RoleSource): boolean {
    return !!source.roles?.includes('tenant_admin');
}

export function hasRole(source: RoleSource, role: UserRole): boolean {
    return !!source.roles?.includes(role);
}

/** Single-axis projection — for display labels and audit-style metadata.
 *  Lossy: a teacher who's also a tenant_admin returns 'admin'. */
export function userTypeFromRoles(source: RoleSource): 'student' | 'teacher' | 'admin' | 'super-admin' {
    if (source.isSuperAdmin) return 'super-admin';
    if (isAdmin(source)) return 'admin';
    if (hasRole(source, 'teacher')) return 'teacher';
    return 'student';
}
